/**
 * The seam between this control plane and the clouds it deploys into.
 *
 * Defined before there is a second cloud, so Azure is an implementation rather
 * than a retrofit. The prototype had the same intent and forwarded through a
 * `Proxy`, which meant a mistyped method name silently resolved to `undefined`
 * and the call did nothing — no compile error, no runtime error, and neither IDE
 * navigation nor TypeScript could see the shape. Every method here is declared,
 * so a missing one fails to compile and an unbuilt one fails loudly.
 *
 * ## What belongs behind it
 *
 * Everything that knows a cloud's vocabulary: role ARNs, CloudFormation, STS,
 * ECS. `api/` and `web/` should be able to say "onboard this account" and
 * "is it healthy" without naming any of that — the handler used to build an IAM
 * ARN itself, which is exactly the kind of knowledge that makes a second cloud a
 * rewrite.
 *
 * ## Why this file is pure
 *
 * `shared` is imported into a browser, so it holds the *port* and no adapter.
 * The AWS adapter lives in `api/`, where the SDK is allowed. That split is also
 * why the contract suite in `contract.ts` is framework-agnostic: it has to run
 * where the adapters live, not where the port is declared.
 */
/**
 * Clouds the adapter layer knows about.
 *
 * A superset of `CLOUDS`, which is the set an account can actually be
 * *registered* in. Azure has an adapter — one that refuses every call — before
 * it has accounts, because a registered-and-refusing adapter is visible while an
 * absent one is not. When Azure onboarding lands it joins `CLOUDS` too, and the
 * exhaustive switches over that union will stop compiling until each is handled,
 * which is the intended alarm.
 */
export const ADAPTER_CLOUDS = ['aws', 'azure'];
/** Every operation the control plane performs against a customer's cloud. */
export const CLOUD_OPERATIONS = [
    'artifact',
    'diagnosticsPrincipal',
    'checkHealth',
    'allowCompletionReports',
    'startRun',
    'runStatus',
    'runLog',
    'onboardingFacts',
    'accountCost',
    'dailyCost',
    'instanceCosts',
    'listRegions',
    'scanInstances',
    'runTargetFacts',
    'diagnosticFacts',
    'instanceResources',
    'clusterState',
    'prometheusQuery',
    'resourceMetric',
    'instancePrices',
    'instanceSpend',
    'boundaryFacts',
    'boundaryArtifact',
    'adoptedStateArtifact',
    'roleBoundaries',
    'buildConfig',
];
/**
 * Thrown by an operation an adapter declares but has not built.
 *
 * Loud on purpose. The alternative — omitting the method — makes the gap
 * invisible at the call site and turns it into `undefined is not a function`
 * somewhere unrelated, which is the prototype's failure mode reproduced.
 */
export class NotImplementedError extends Error {
    cloud;
    operation;
    tracking;
    constructor(cloud, operation, 
    /** The story that lands it, so the message says what to do about it. */
    tracking) {
        super(`${operation} is not implemented for ${cloud} (${tracking})`);
        this.cloud = cloud;
        this.operation = operation;
        this.tracking = tracking;
        this.name = 'NotImplementedError';
    }
}
/**
 * Thrown by an operation that has no meaning on this cloud, and never will.
 *
 * Different from `NotImplementedError`, which says "coming, in this story":
 * a permissions boundary is an IAM idea, and an Azure adapter asked for one has
 * nothing to build, now or later. Keeping the two apart is what lets a caller
 * answer each honestly — a gap to wait for is not a thing that does not exist —
 * and stops a list of unbuilt operations quietly filling with ones nobody
 * intends to build.
 */
export class NotApplicableError extends Error {
    cloud;
    operation;
    reason;
    constructor(cloud, operation, 
    /** Why, in words a screen can print: what this cloud has instead, if anything. */
    reason) {
        super(`${operation} does not apply to ${cloud}: ${reason}`);
        this.cloud = cloud;
        this.operation = operation;
        this.reason = reason;
        this.name = 'NotApplicableError';
    }
}
/**
 * What the AWS adapter hands a customer, by the part each plays (IM-93, IM-94).
 *
 * Named by role rather than by stack name, because the customer chooses the
 * name — which is why a stack is found by what it publishes (IM-115). The split
 * is IAM's: a grant stack exists because the runner and the deploy role are
 * separate IAM roles with a time condition between them, and a region stack
 * because Terraform's S3 backend is per region and IAM role names are global.
 */
export const AWS_ARTIFACT_KINDS = ['access', 'grant', 'region', 'dispatcher'];
/**
 * What the Azure adapter hands a customer (IM-45), which is a different
 * decomposition rather than the same four under other names.
 *
 * - **delegation** — the Lighthouse delegation (IM-44): Reader for Qrvey's
 *   diagnostics app, carrying the account's mark. The trust boundary, and so
 *   the one `secret` kind, served from `…/bootstrap` like the AWS access
 *   template. Under resource-group scope it is deployed once per group.
 * - **deploy-access** — an `az` script that creates the deploy app registration
 *   and mints its one client secret, whose expiry *is* the grant. A script,
 *   because ARM cannot create an Entra application.
 * - **runner** — the Container Apps job, its identity, the Key Vault, the run
 *   log workspace and the dispatcher Logic App. Once per subscription: none of
 *   it has to be in the region an instance is deployed to.
 * - **instance** — the instance's resource group, its state storage and the
 *   deploy app's role assignments on that group. Once per instance, because the
 *   installer keeps state and instance in one group.
 *
 * CLAUDE.md's *Azure* section has the reasoning, including why there is no
 * per-region artifact at all.
 */
export const AZURE_ARTIFACT_KINDS = ['delegation', 'deploy-access', 'runner', 'instance'];
export const ARTIFACT_KINDS = [...AWS_ARTIFACT_KINDS, ...AZURE_ARTIFACT_KINDS];
/**
 * The account cannot be reached for this owner: it is not registered to them,
 * or it has been hidden (IM-108).
 *
 * Callers check both before asking, from the account listing, and answer 404
 * themselves — so this is the adapter refusing to reach an account nobody is
 * supposed to be operating, not a path a request is expected to take.
 */
export class AccountUnreachableError extends Error {
    accountKey;
    constructor(accountKey) {
        super(`${accountKey} is not registered to this customer, or has been hidden`);
        this.accountKey = accountKey;
        this.name = 'AccountUnreachableError';
    }
}
/**
 * A log that cannot be read yet, or at all.
 *
 * Not a failure of the run: a stream appears when the task writes its first
 * line, and it ages out of the customer's retention window eventually. The
 * caller shows the reason rather than an error.
 */
export class RunLogUnavailableError extends Error {
    reason;
    constructor(reason, detail) {
        super(detail);
        this.reason = reason;
        this.name = 'RunLogUnavailableError';
    }
}
/** A run the customer's account refused to start, with the reason it gave. */
export class RunRefusedError extends Error {
    reason;
    detail;
    constructor(reason, detail) {
        super(detail === undefined ? reason : `${reason}: ${detail}`);
        this.reason = reason;
        this.detail = detail;
        this.name = 'RunRefusedError';
    }
}
export class UnknownCloudError extends Error {
}
/**
 * Resolves an adapter, refusing anything not registered.
 *
 * Takes the narrow `Cloud` — the set an account can be registered in — so a
 * caller holding a `CloudAccount` cannot ask for a cloud that has no accounts.
 */
export const adapterFor = (adapters, cloud) => {
    const adapter = adapters[cloud];
    if (adapter === undefined)
        throw new UnknownCloudError(`no adapter registered for ${cloud}`);
    return adapter;
};
//# sourceMappingURL=adapter.js.map